/** @format */
import { inject, Pipe, PipeTransform } from '@angular/core';
import { OtmId } from 'common-typescript/dist/typings/types';
import { DegreeProgramme, GroupingModule, Module, StudyModule } from 'common-typescript/types';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppErrorHandler } from '../error-handler/app-error-handler';
import { ModuleEntityService } from '../service/module-entity.service';

// add new allowed Module subtypes here
const allowedSubtypes = ['DegreeProgramme', 'GroupingModule', 'StudyModule'] as const;
export type ModuleSubtype = (typeof allowedSubtypes)[number];

/**
 * Returns `Module` object or its subtype.
 * Desired subtype is specified by the `type` parameter, which is optional.
 * If `Module` object cannot be cast to the desired subtype, an error is thrown.
 */
@Pipe({
    name: 'module',
    standalone: false,
})
export class ModulePipe implements PipeTransform {
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly moduleEntityService = inject(ModuleEntityService);

    transform(id: OtmId, type: 'DegreeProgramme'): Observable<DegreeProgramme>;
    transform(id: OtmId, type: 'GroupingModule'): Observable<GroupingModule>;
    transform(id: OtmId, type: 'StudyModule'): Observable<StudyModule>;
    transform(id: OtmId): Observable<Module>;
    transform(id: OtmId, type?: ModuleSubtype): Observable<Module | DegreeProgramme | GroupingModule | StudyModule> {
        if (!id) {
            return null;
        }
        return this.moduleEntityService.getById(id).pipe(
            map(module => this.castModule(module, type)),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    private castModule(module: Module, type?: ModuleSubtype): Module | DegreeProgramme | GroupingModule | StudyModule {
        if (!type) {
            return module; // no casting needed, return Module object
        }
        if (type !== module.type) {
            throw new Error(`Module cannot be cast to type ${type}, because it is of type ${module.type}`);
        }
        if (!allowedSubtypes.includes(type)) {
            // this is a developer error, because the allowedSubtypes array should be updated
            throw new Error(`Invalid module type: ${type}`);
        }
        switch (type) {
            case 'DegreeProgramme':
                return module as DegreeProgramme;
            case 'GroupingModule':
                return module as GroupingModule;
            case 'StudyModule':
                return module as StudyModule;
            default:
                throw new Error(`Casting to subtype ${type} has not been implemented`);
        }
    }
}
